import React from 'react';
import { CardBody, CardTitle, Input } from 'reactstrap';
import cls from 'classnames';
import { faMinusCircle } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { INTERPUNCT } from '../common/util';
import { SmallButton } from '../common/forms/FormElements';

const RESOURCE_TYPE = {
    STAFF: 'staff',
    EQUIPMENT: 'equipment',
};

export default class EquipmentAssignmentCard extends React.Component {
    setRef = (ref) => {
        // keep a reference to the dom ref as an instance property
        this.ref = ref;
        // give the dom ref to @hello-pangea/dnd
        this.props.innerRef(ref);
    };

    getStatusName = () => this.props.requiredEquipment.confirmationStatusName
        .replace(' ', '')
        .toLowerCase();

    renderEquipmentAssignmentOptions = () => {
        const {
            dayAssignments, // all assignments.  we need this to see what has been assigned thus far today.
            requiredEquipment, // the currently required equipment of the job resource.
            job, // index from the job, so we can look it up (BDnD doesnt do objects)
            day, // day of week
            assignmentIndex,
            dispatchEditPermission, // assignment index, so we can look it up (BDnD doesnt do objects).
        } = { ...this.props };

        // The current job assignment object
        const assignment = dayAssignments.equipment[assignmentIndex];

        // does this have required equipment?
        if (
            (
                requiredEquipment /* The type of equipment required by the job resource */
                    .equipmentType /* The base equipment type */
                    .requiredEquipment
                ?? /* Equipment that is required BY THE BASE equipment type....i.e. Arrow Board that requires a Work Truck to get around. */[]
            ).length
        ) {
            /* Do we have any */ // get all of this equipment's required equipment Ids.
            const requiredEquipmentTypeIds = requiredEquipment.equipmentType.requiredEquipment.map(
                (x) => x.id,
            );

            // get all equipment assignments for the day .
            const assignedEquipmentToday = dayAssignments.equipment
                .filter(
                    (x) => x.id
                        !== (assignment ?? {})
                            .id
                        && /* (don't include the current assignment) */ !!x.assignment,
                )
                .map((e) => e.assignment);

            // filter the day's equipment assignments by the required equipment type, so the user can assign the required dependency.
            const options = assignedEquipmentToday.filter((x) => requiredEquipmentTypeIds.includes(x.equipmentTypeId));


            // display the dropdown only when theres something to show.
            return (
                <div className="d-flex flex-column">

                    <Input
                        type="select"
                        placeholder="Assign Equipment"
                        className={cls({
                            'text-success':
                                !!assignment?.assignedEquipment?.id,
                            'text-muted':
                                !assignment?.assignedEquipment?.id,
                        })}
                        style={{ backgroundColor: (!dispatchEditPermission) ? 'transparent' : undefined }}
                        value={assignment?.assignedEquipment?.id ?? ''}
                        onChange={(event) => {
                            if (!event.target.value) {
                                event.stopPropagation();
                                return false;
                            }
                            const resourceId = parseInt(event.target.value, 10);
                            this.props.onEquipmentAssignmentChange(
                                job.id,
                                day.getDay(),
                                RESOURCE_TYPE.EQUIPMENT,
                                assignmentIndex,
                                resourceId,
                            );
                        }}
                        disabled={!dispatchEditPermission}
                    >
                        <option
                            className="text-muted"
                            value=""
                        >
                            Assign Equipment
                        </option>
                        {options.map((o) => (
                            <option
                                key={o.id}
                                value={o.id}
                            >
                                {`${o.description} (${o.equipmentType.description})`}
                            </option>
                        ))}
                    </Input>

                    <span
                        className={`assignment-status-label text-right ${this.getStatusName()} mr-2`}
                    >
                        {requiredEquipment.confirmationStatusName}
                    </span>
                </div>
            );
        }
    };

    getQualifiedStaff = () => {
        const {
            dayAssignments,
            requiredEquipment,
        } = { ...this.props };

        // does this have required groups?
        if ((requiredEquipment.equipmentType.groups ?? []).length) {
            // grab objects for all staff that's been assigned for the day
            const todayAssignedStaff = dayAssignments.staff
                .filter((a) => !!a.assignment)
                .map((a) => a.assignment);

            // Get any employee groups required by the equipment
            const requiredEmpGroups = requiredEquipment.equipmentType.groups.map(
                (eg) => eg.id,
            );

            const qualifiedStaff = (todayAssignedStaff ?? []).filter((s) => {
                // filter the day's staff by the required emp groups.
                const empGroupIds = s.groups.map((grp) => grp.id);
                const hasGroup = empGroupIds.some((groupId) => requiredEmpGroups.includes(groupId));
                return hasGroup;
            });

            return qualifiedStaff;
        }
        return [];
    };

    renderStaffAssignmentOptions = () => {
        const {
            dayAssignments,
            requiredEquipment,
            job,
            day,
            assignmentIndex,
            dispatchEditPermission,
        } = { ...this.props };

        const assignment = dayAssignments.equipment[assignmentIndex];
        const qualifiedStaff = this.getQualifiedStaff();

        return (
            <div className="d-flex flex-column">
                <Input
                    type="select"
                    placeholder="Assign Staff"
                    className={cls({
                        'text-success': !!assignment?.assignedEmployee?.id,
                        'text-muted': !assignment?.assignedEmployee?.id,
                    })}
                    style={{ backgroundColor: (!dispatchEditPermission) ? 'transparent' : undefined }}
                    value={assignment?.assignedEmployee?.id ?? ''}
                    onChange={(event) => {
                        if (!event.target.value) {
                            event.stopPropagation();
                            return false;
                        }
                        const resourceId = parseInt(event.target.value, 10);
                        this.props.onEquipmentAssignmentChange(
                            job.id,
                            day.getDay(),
                            RESOURCE_TYPE.STAFF,
                            assignmentIndex,
                            resourceId,
                        );
                    }}
                    disabled={!dispatchEditPermission}
                >
                    <option
                        className="text-muted"
                        value=""
                    >
                        Assign Staff
                    </option>
                    {qualifiedStaff.map((o) => (
                        <option
                            key={o.id}
                            value={o.id}
                        >
                            {o.name}
                        </option>
                    ))}
                </Input>

                <span
                    className={`assignment-status-label text-right ${this.getStatusName()} mr-2`}
                >
                    {requiredEquipment.confirmationStatusName}
                </span>
            </div>
        );
    };

    renderCombinedAssignmentOptions = () => {
        const {
            dayAssignments,
            requiredEquipment,
            job,
            day,
            assignmentIndex,
            dispatchEditPermission,
        } = { ...this.props };

        const assignment = dayAssignments.equipment[assignmentIndex];

        const qualifiedStaff = this.getQualifiedStaff();
        const requiredEquipmentTypeIds = (requiredEquipment.equipmentType.requiredEquipment ?? []).map(
            (x) => x.id,
        );
        const assignedEquipmentToday = dayAssignments.equipment
            .filter(
                (x) => x.id !== (assignment ?? {}).id && !!x.assignment,
            )
            .map((e) => e.assignment);
        const qualifiedEquipment = assignedEquipmentToday.filter((x) =>
            requiredEquipmentTypeIds.includes(x.equipmentTypeId),
        );

        let currentValue = '';
        if (assignment?.assignedEmployee?.id) {
            currentValue = `${RESOURCE_TYPE.STAFF}-${assignment.assignedEmployee.id}`;
        } else if (assignment?.assignedEquipment?.id) {
            currentValue = `${RESOURCE_TYPE.EQUIPMENT}-${assignment.assignedEquipment.id}`;
        }

        return (
            <div className="d-flex flex-column">
                <Input
                    type="select"
                    placeholder="Assign"
                    className={cls({
                        'text-success': !!assignment?.assignedEmployee?.id || !!assignment?.assignedEquipment?.id,
                        'text-muted': !assignment?.assignedEmployee?.id && !assignment?.assignedEquipment?.id,
                    })}
                    style={{ backgroundColor: (!dispatchEditPermission) ? 'transparent' : undefined }}
                    value={currentValue}
                    onChange={(event) => {
                        if (!event.target.value) {
                            event.stopPropagation();
                            return false;
                        }
                        const parts = event.target.value.split('-');
                        if (parts.length !== 2) {
                            console.error('Invalid assignment value format:', event.target.value);
                            return false;
                        }
                        const [type, idStr] = parts;
                        if (type !== RESOURCE_TYPE.STAFF && type !== RESOURCE_TYPE.EQUIPMENT) {
                            console.error('Invalid resource type:', type);
                            return false;
                        }
                        const resourceId = parseInt(idStr, 10);
                        if (isNaN(resourceId)) {
                            console.error('Invalid resource ID:', idStr);
                            return false;
                        }
                        this.props.onEquipmentAssignmentChange(
                            job.id,
                            day.getDay(),
                            type,
                            assignmentIndex,
                            resourceId,
                        );
                    }}
                    disabled={!dispatchEditPermission}
                >
                    <option
                        className="text-muted"
                        value=""
                    >
                        Assign
                    </option>

                    {qualifiedStaff.length > 0 && (
                        <optgroup label="Staff">
                            {qualifiedStaff.map((o) => (
                                <option
                                    key={`${RESOURCE_TYPE.STAFF}-${o.id}`}
                                    value={`${RESOURCE_TYPE.STAFF}-${o.id}`}
                                >
                                    {o.name}
                                </option>
                            ))}
                        </optgroup>
                    )}

                    {qualifiedEquipment.length > 0 && (
                        <optgroup label="Equipment">
                            {qualifiedEquipment.map((o) => (
                                <option
                                    key={`${RESOURCE_TYPE.EQUIPMENT}-${o.id}`}
                                    value={`${RESOURCE_TYPE.EQUIPMENT}-${o.id}`}
                                >
                                    {`${o.description} (${o.equipmentType.description})`}
                                </option>
                            ))}
                        </optgroup>
                    )}
                </Input>

                <span
                    className={`assignment-status-label text-right ${this.getStatusName()} mr-2`}
                >
                    {requiredEquipment.confirmationStatusName}
                </span>
            </div>
        );
    };

    render() {
        const {
            job, requiredEquipment, assignmentRequirements, day, dispatchEditPermission,
        } = {
            ...this.props,
        };

        const dayInt = day.getDay();

        const canAssignToEquipment = !!(
            requiredEquipment.equipmentType.requiredEquipment ?? []
        ).length;

        const canAssignToStaff = !!(
            requiredEquipment.equipmentType.groups ?? []
        ).length;

        const useCombinedDropdown = canAssignToStaff && canAssignToEquipment;

        return (
            <div className={cls('card equipmentCard compact', this.getStatusName())}>
                <CardBody>
                    <CardTitle className="text-left d-flex flex-row justify-content-between align-items-center">
                        <div className="d-flex flex-row flex-fill flex-nowrap justify-content-between align-items-center">
                            <span className="d-flex flex-column">
                                <div>
                                    <strong className="text-muted">
                                        {requiredEquipment.equipmentType.description}
                                    </strong>

                                    {assignmentRequirements.isRental ? (
                                        <>
                                            {!requiredEquipment.equipmentType.isGeneric ? (
                                                <>
                                                    {` ${INTERPUNCT} `}
                                                    {requiredEquipment.description}

                                                    <div>
                                                        {assignmentRequirements.isRentalPickup ? 'Pickup' : 'Dropoff'}
                                                    </div>
                                                </>
                                            ) : (
                                                <>
                                                    {assignmentRequirements.rentalDropoffQuantity > 0 && (
                                                        <div>
                                                            Dropoff:
                                                            {' '}
                                                            {assignmentRequirements.rentalDropoffQuantity}
                                                        </div>
                                                    )}

                                                    {assignmentRequirements.rentalPickupQuantity > 0 && (
                                                        <div>
                                                            Pickup:
                                                            {' '}
                                                            {assignmentRequirements.rentalPickupQuantity}
                                                        </div>
                                                    )}
                                                </>
                                            )}
                                        </>
                                    ) : (
                                        <>
                                            {` ${INTERPUNCT} `}
                                            {requiredEquipment.equipmentType.isGeneric
                                                ? ` ${requiredEquipment.description} x ( ${assignmentRequirements?.quantity} ) `
                                                : ' ' + requiredEquipment.description
                                            }
                                        </>
                                    )}

                                    {requiredEquipment.dispatchLocation.id !== job.dispatchLocationId && (
                                        <span className="d-block">Dispatched from {requiredEquipment.dispatchLocation.name}</span>
                                    )}

                                </div>
                            </span>

                            <div className="assignmentDropdown">
                                {useCombinedDropdown && this.renderCombinedAssignmentOptions()}
                                {!useCombinedDropdown && canAssignToEquipment && this.renderEquipmentAssignmentOptions()}
                                {!useCombinedDropdown && canAssignToStaff && !canAssignToEquipment && this.renderStaffAssignmentOptions()}
                            </div>
                        </div>
                        {
                            // job.canEdit is backed by whether or not the crew has submitted a timesheet.
                            // if we have a timesheet for this crew for this day, we cannot remove the assignment.
                            !!this.props.removable
                            && !!job.assignments[dayInt].canEdit && (
                                <SmallButton
                                    color="link"
                                    title="Remove this equipment"
                                    disabled={!dispatchEditPermission}
                                    className="p-0"
                                    onClick={() => {
                                        this.props.onRemoveAssignment(
                                            job,
                                            assignmentRequirements,
                                            dayInt,
                                        );
                                    }}
                                >
                                    <FontAwesomeIcon
                                        icon={faMinusCircle}
                                        className="text-danger"
                                    />
                                </SmallButton>
                            )
                        }
                    </CardTitle>
                </CardBody>
            </div>
        );
    }
}
